import React, { useState, useContext, useEffect } from "react";
import emailStar from "../../../assets/images/organisationProfile/pricing/emailStar.svg";
import proStar from "../../../assets/images/organisationProfile/pricing/proStar.svg";
import "./pricing.scss";
import Button from "components/Button/index";
import Success from "components/success/index";
import { useToast } from "components/Toaster/index";
import { signupContext } from "context/SignupContext";
import { StoragePlan } from "@induz/sdk";
import { useNavigate } from "react-router-dom";
import useScreenMobile from "hooks/useScreen";

const FeatureItem = ({ icon, text }) => (
  <div className="folder">
    <img src={icon} alt="feature icon" />
    <p>{text}</p>
  </div>
);

function Pricing({ setChange, setSuccess, success, setSelectPrice, nextStep, setSelectedPlanIndex, setCanGoNext }) {
  const {
    setPaymentKey,
    setOrderId,
    setAmount,
    setCurrency,
    setPaymentObj,
    setIsTypeSelect,
    setSelectedPlanData,
    selectedPlanData
  } = useContext(signupContext);

  useEffect(() => {
    if (setCanGoNext) setCanGoNext(!!selectedPlanData);
  }, [selectedPlanData, setCanGoNext]);

  const showToast = useToast();
  const navigate = useNavigate();
  const isMobile = useScreenMobile({ size: 768 });
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [userType, setUserType] = useState("org");
  const [loading, setLoading] = useState(true);
  const [activePlan, setActivePlan] = useState(null);

  // No auto-set for popup layout, start with list
  useEffect(() => {
    if (!isMobile) {
      setActivePlan(null);
    }
  }, [isMobile]);

  const formatPrice = (price) =>
    typeof price === "number"
      ? `₹${new Intl.NumberFormat("en-IN").format(price)}`
      : price;

  const getPlanPrice = (basePrice) => {
    if (typeof basePrice !== "number") return basePrice;
    const multiplier = userType === "individual" || userType === "ind" ? 10 : 11;
    return billingPeriod === "monthly" ? basePrice : basePrice * multiplier;
  };

  // Helper function to format bytes to human-readable format
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${Math.round(value * 100) / 100} ${sizes[i]}`;
  };

  // Helper function to convert Long to number
  const toNumber = (value) => {
    if (typeof value === "number") return value;
    if (value && typeof value.toNumber === "function") return value.toNumber();
    if (value && typeof value.toString === "function") return parseInt(value.toString(), 10);
    return 0;
  };

  // Transform StoragePlan to component format using backend data
  const transformPlan = (storagePlan, index, isOrg) => {
    
    const price = toNumber(storagePlan.getPrice());
    const storageBytes = toNumber(storagePlan.getStorage()); // Backend returns bytes
    const maxSpaces = toNumber(storagePlan.getMaxSpaces());
    const minUsers = toNumber(storagePlan.getMinUsers());
    const maxRoles = storagePlan.getMaxRoles ? toNumber(storagePlan.getMaxRoles()) : null;
    console.log(`[Plan ${index}] maxRoles:`, maxRoles);
    const maxTeams = storagePlan.getMaxTeams ? toNumber(storagePlan.getMaxTeams()) : null;
    const maxAlerts = toNumber(storagePlan.getMaxAlerts());
    const logsHistoryDays = toNumber(storagePlan.getLogsHistory());
    const trashFromPlan = toNumber(storagePlan.getTrash?.());
    const maxTags = toNumber(storagePlan.getTags());
    const maxDevices = storagePlan.getMaxDevices ? storagePlan.getMaxDevices() : 0;
    const parallelEdits = storagePlan.getParallelEdits ? toNumber(storagePlan.getParallelEdits()) : 0;
    const maxVersions = storagePlan.getMaxVersions ? toNumber(storagePlan.getMaxVersions()) : null;
    const planName = storagePlan.getname() || `Plan ${index + 1}`;
    const planId = storagePlan.getPlanId();
    const redundancy = storagePlan.getRedundancy ? storagePlan.getRedundancy() : "LRS";
    const support = storagePlan.getSupport ? storagePlan.getSupport() : "Regular";
    const ransomwareProtection = storagePlan.getRansomwareProtection ? storagePlan.getRansomwareProtection() : false;
    const collaboration = storagePlan.getCollaboration ? storagePlan.getCollaboration() : false;
    const internalSharing = storagePlan.getInternalSharing ? storagePlan.getInternalSharing() : false;
    const tagBasedAnalysis = storagePlan.getTagBasedAnalysis ? storagePlan.getTagBasedAnalysis() : false;
    const complianceReports = storagePlan.getComplianceReports ? storagePlan.getComplianceReports() : false;
    const geoLocationAlerts = storagePlan.getGeoLocationBasedAlerts ? storagePlan.getGeoLocationBasedAlerts() : false;
    const dedicatedCluster = storagePlan.getDedicatedCluster ? storagePlan.getDedicatedCluster() : false;


    // Convert bytes to human-readable format (GB, TB, PB)
    const storageFormatted = formatBytes(storageBytes);

    // Build features array based on backend plan properties
    const features = [];

    if (ransomwareProtection) {
      if (collaboration && internalSharing && isOrg) {
        features.push("* Ransomware protection + collaboration suite");
      } else {
        features.push("Ransomware protection");
      }
    } else if (collaboration && internalSharing && isOrg) {
      features.push("* Collaboration, internal sharing, docs & sheets");
    }

    if (parallelEdits > 0) {
      // Use maxVersions from backend if available, otherwise calculate from logsHistory
      const versions = maxVersions !== null && maxVersions !== undefined && maxVersions > 0
        ? maxVersions
        : (logsHistoryDays > 0 ? Math.floor(logsHistoryDays / 30) : 10);
      features.push(`Parallel edits: ${parallelEdits} * • Versions: ${versions}`);
    }

    if (isOrg) {
      // Organization-specific features
      if (maxAlerts > 0) {
        const ysentinelMonths = logsHistoryDays > 0 ? Math.floor(logsHistoryDays / 30) : 3;
        features.push(`Ysentinel period: ${ysentinelMonths} months`);
        features.push(`* Configurable alerts: ${maxAlerts} • Log period: ${Math.floor(logsHistoryDays / 30)} months`);
      }

      if (maxTags > 0) {
        const tagText = tagBasedAnalysis
          ? `Max tags per file: ${maxTags} • Tag based analysis *`
          : `Max tags per file: ${maxTags}`;
        features.push(tagText);
      }

      if (maxRoles !== null && maxRoles > 0) {
        features.push(`Max roles: ${maxRoles}`);
      
      }

      if (complianceReports) {
        features.push(`Compliance reports: 10 / month *${geoLocationAlerts ? " • Geo-location alerts *" : ""}`);
      }

      if (dedicatedCluster) {
        features.push("Compliance + geo alerts * • Dedicated cluster");
        features.push("Designed for regulated workloads & throughput");
      }
    } else {
      // Individual-specific features - Align with Image
      if (maxAlerts > 0) {
        const lowerName = planName.toLowerCase();
        let ysentinelPerDay;
        
        if (lowerName.includes("pro")) {
          ysentinelPerDay = lowerName.includes("ultra") ? 500 : 100;
        } else {
          ysentinelPerDay = Math.floor(maxAlerts / (logsHistoryDays || 90));
        }

        features.push(`* Ysentinel: ${ysentinelPerDay}/day • Configurable alerts: ${maxAlerts}`);
      }

      if (logsHistoryDays > 0 && index >= 2) {
        features.push(`Log period: ${Math.floor(logsHistoryDays / 30)} months`);
      }

      // Show tags for all individual plans if available (Free has 2 in image)
      if (maxTags > 0) {
        features.push(`Max tags per file: ${maxTags}`);
      }

      if (maxRoles !== null && maxRoles > 0) {
        features.push(`Max roles: ${maxRoles}`);
      }

      // if (index === 0) {
      //   features.push("Ledger logs");
      // }
    }

    features.push(`Trash retention: ${trashFromPlan}`);

    // Determine badge based on index (for org plans)
    let badge = null;
    if (isOrg) {
      if (index === 1) {
        badge = "Popular";
      } else if (index === 2) {
        badge = "Scale";
      } else if (index === 3) {
        badge = "Enterprise";
      }
    }

    // Format storage for display (already converted above)
    const spacesFormatted = maxSpaces > 0 ? `${maxSpaces} spaces` : "Unlimited";
    const usersFormatted = isOrg
      ? (minUsers > 0 ? `Up to ${minUsers} users` : "Unlimited users")
      : (maxDevices > 0 ? maxDevices.toString() : "3");

    // Check if it's a contact plan (usually the highest tier org plan)
    const isContact = isOrg && index === 3 && (price === 0 || planName.toLowerCase().includes("enterprise") || planName.toLowerCase().includes("ultra pro"));

    const stats = [
      { label: "Redundancy", value: redundancy || "LRS" },
      { label: "Support", value: support || "Regular" },
      { label: "Trash", value: String(trashFromPlan) },
      ...(maxRoles !== null && maxRoles > 0 ? [{ label: "Roles", value: String(maxRoles) }] : []),
      ...(maxTeams !== null && maxTeams > 0 ? [{ label: "Teams", value: String(maxTeams) }] : [])
    ];

    return {
      name: planName,
      price: isContact ? "Contact us" : price,
      planId: planId,
      trashRetentionDays: trashFromPlan,
      maxRoles: maxRoles,
      storage: {
        user: storageFormatted,
        team: spacesFormatted,
        users: usersFormatted
      },
      stats,
      features: features.length > 0 ? features : ["Standard features included"],
      buttonText: isContact ? "Talk to us" : (price === 0 ? "Get Started" : (index === 1 && isOrg ? "Start Pro" : (index === 2 && isOrg ? "Choose Ultra Plus" : "Get Started"))),
      badge: badge,
      isContact: isContact,
      matcher: planName,
      onClick: isContact ? () => window.location.href = "mailto:sales@induz.com" : undefined
    };
  };


  const ORGANISATION_PLANS_CONFIG = [
    {
      name: "Organisation Basic",
      price: 4000,
      badge: null,
      storage: {
        users: "Up to 20 users",
        user: "500 GB",
        team: "10 spaces"
      },
      stats: [
        { label: "Redundancy", value: "LRS" },
        { label: "Support", value: "Regular" }
      ],
      features: [
        "Ransomware protection",
        "Collaboration, internal sharing, docs & sheets",
        "Parallel edits: 20 * • Versions: 10",
        "Ysentinel period: 3 months",
        "Configurable alerts: 3 • Log period: 3 months"
      ],
      buttonText: "Get Started",
      matcher: "Basic"
    },
    {
      name: "Organisation Pro",
      price: 10000,
      badge: "Popular",
      storage: {
        users: "Up to 100 users",
        user: "1 TB",
        team: "200 spaces"
      },
      stats: [
        { label: "Redundancy", value: "ZRS" },
        { label: "Support", value: "Advanced" }
      ],
      features: [
        "Ransomware protection + collaboration suite",
        "Parallel edits: 100 * • Versions: 20",
        "Ysentinel period: 6 months",
        "Configurable alerts: 6 • Log period: 12 months",
        "Max tags per file: 20 • Tag based analysis *"
      ],
      buttonText: "Start Pro",
      matcher: "Pro"
    },
    {
      name: "Organisation Ultra Plus",
      price: 100000,
      badge: "Scale",
      storage: {
        users: "Up to 1000 users",
        user: "20 TB",
        team: "500 spaces"
      },
      stats: [
        { label: "Redundancy", value: "ZRS" },
        { label: "Support", value: "One-day" }
      ],
      features: [
        "Advanced collaboration & ransomware protection",
        "Parallel edits: 500 * • Versions: 50",
        "Ysentinel period: 12 months",
        "Configurable alerts: 12 • Log period: 24 months",
        "Compliance reports: 10 / month * • Geo-location alerts *"
      ],
      buttonText: "Choose Ultra Plus",
      matcher: "Ultra Plus"
    },
    {
      name: "Organisation Ultra Pro",
      price: "Contact us",
      badge: "Enterprise",
      storage: {
        users: "Unlimited users",
        user: "1 PB",
        team: "Unlimited"
      },
      stats: [
        { label: "Redundancy", value: "ZRS" },
        { label: "Support", value: "One-day priority" }
      ],
      features: [
        "Unlimited parallel edits * • Versions: 100",
        "Ysentinel period: 24 months • Alerts: 1000",
        "Log period: 24 months • Ledger logs",
        "Compliance + geo alerts * • Dedicated cluster",
        "Designed for regulated workloads & throughput"
      ],
      buttonText: "Talk to us",
      isContact: true,
      matcher: "Ultra Pro"
    }
  ];

  const INDIVIDUAL_PLANS_CONFIG = [
    {
      name: "Free",
      price: 0,
      badge: null,
      storage: {
        users: "3",
        user: "8 GB",
        team: "5 spaces"
      },
      stats: [
        { label: "Redundancy", value: "LRS" },
        { label: "Support", value: "Regular" }
      ],
      features: [
        "Ransomware protection",
        "Parallel edits: 5 * • Versions: 10",
        "Max tags per file: 2",
        // "Ledger logs"
      ],
      buttonText: "Get Started",
      matcher: "Free"
    },
    {
      name: "Basic",
      price: 150,
      badge: null,
      storage: {
        users: "5",
        user: "100 GB",
        team: "25 spaces"
      },
      stats: [
        { label: "Redundancy", value: "LRS" },
        { label: "Support", value: "Regular" }
      ],
      features: [
        "Ransomware protection",
        "Parallel edits: 5 * • Versions: 10",
        "Max tags per file: 10"
      ],
      buttonText: "Get Started",
      matcher: "Basic"
    },
    {
      name: "Pro",
      price: 700,
      badge: null,
      storage: {
        users: "10",
        user: "500 GB",
        team: "50 spaces"
      },
      stats: [
        { label: "Redundancy", value: "LRS" },
        { label: "Support", value: "Regular" }
      ],
      features: [
        "Ransomware protection",
        "Parallel edits: 20 * • Versions: 10",
        "* Ysentinel: 100/day • Configurable alerts: 50",
        "Log period: 3 months",
        "Max tags per file: 10"
      ],
      buttonText: "Get Started",
      matcher: "Pro"
    },
    {
      name: "Ultra Pro",
      price: 1500,
      badge: null,
      storage: {
        users: "10",
        user: "1 TB",
        team: "100 spaces"
      },
      stats: [
        { label: "Redundancy", value: "ZRS" },
        { label: "Support", value: "One Day" }
      ],
      features: [
        "Ransomware protection",
        "Parallel edits: 100 * • Versions: 100",
        "* Ysentinel: 500/day • Configurable alerts: 100",
        "Log period: 6 months",
        "Max tags per file: 100"
      ],
      buttonText: "Get Started",
      matcher: "Ultra Pro"
    }
  ];

  // Fetch plans from SDK and transform backend data to UI format
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const storagePlan = new StoragePlan();
        const customerType = userType === "individual" ? "ind" : "org";
        console.log("Fetching plans for user type:", userType, "customerType:", customerType);

        const backendPlansList = await storagePlan.listPlans(customerType);
        console.log("Backend plans received:", backendPlansList);

        // Filter plans by type and transform to UI format
        const filteredPlans = backendPlansList
          .filter(p => {
            const planType = p.getType();
            return planType === customerType;
          })
          .map((plan, index) => transformPlan(plan, index, userType === "org"));

        // Sort plans by price (ascending) to maintain consistent order
        filteredPlans.sort((a, b) => {
          const priceA = typeof a.price === "number" ? a.price : Infinity;
          const priceB = typeof b.price === "number" ? b.price : Infinity;
          return priceA - priceB;
        });

        if (filteredPlans.length > 0) {
          console.log("Transformed plans:", filteredPlans.map(p => ({
            name: p.name,
            planId: p.planId,
            price: p.price,
            trashRetentionDays: p.trashRetentionDays,
            maxRoles: p.maxRoles
          })));
          setPlans(filteredPlans);
        } else {
          console.warn("No plans found for type:", customerType, "Falling back to hardcoded configs");
          // Fallback to hardcoded configs if no backend plans
          const fallbackPlans = userType === "org" ? ORGANISATION_PLANS_CONFIG : INDIVIDUAL_PLANS_CONFIG;
          setPlans(fallbackPlans.map(plan => ({
            ...plan,
            onClick: plan.isContact ? () => window.location.href = "mailto:sales@induz.com" : undefined
          })));
        }
      } catch (error) {
        console.error("Failed to fetch plans from backend:", error);
        // Fallback to hardcoded configs on error
        const fallbackPlans = userType === "org" ? ORGANISATION_PLANS_CONFIG : INDIVIDUAL_PLANS_CONFIG;
        setPlans(fallbackPlans.map(plan => ({
          ...plan,
          onClick: plan.isContact ? () => window.location.href = "mailto:sales@induz.com" : undefined
        })));
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [showToast, userType]);

  const handleUserTypeChange = (type) => {
    setUserType(type);
    if (setIsTypeSelect) {
      setIsTypeSelect(type === "org" ? "org" : "individual");
    }
  };

  const paymentHandler = async (planIndex) => {
    const selectedPlan = plans[planIndex];
    const price = getPlanPrice(selectedPlan.price);

    console.log("[Pricing] plan selected for checkout", {
      name: selectedPlan.name,
      planId: selectedPlan.planId,
      trashRetentionDays: selectedPlan.trashRetentionDays,
      maxRoles: selectedPlan.maxRoles
    });

    const planData = {
      ...selectedPlan,
      price: price,
      originalPrice: selectedPlan.price,
      planId: selectedPlan.planId, // Store planId from backend for startPayment()
      billingPeriod: billingPeriod,
    };

    if (setSelectPrice) {
      setSelectPrice(planData);
    }
    if (setSelectedPlanData) {
      setSelectedPlanData(planData);
    }

    if (setSelectedPlanIndex) {
      setSelectedPlanIndex(planIndex);
    }
    if (setIsTypeSelect) {
      setIsTypeSelect(userType === "org" ? "org" : "individual");
    }

    if (nextStep) {
      nextStep(2);
    } else {
      setChange("payment");
    }
  };

  const addUserHandler = () => {
    nextStep(7);
  };

  return (
    <>
      {!success ? (
        <div className="Pricing">
          <div className="PricingMain">
            <div className="PricingPlans">
              <h1>Pricing Plans</h1>
              {/* <p>
                Our pricing is designed to offer flexible plans that cater to
                your needs and budget.
              </p> */}
            </div>
            <div className="PricingControlsRow">
              <div className="PricingUserType">
                <button
                  type="button"
                  className={`userTypeTab ${userType === "individual" ? "active" : ""}`}
                  onClick={() => handleUserTypeChange("individual")}
                >
                  Individual
                </button>
                <button
                  type="button"
                  className={`userTypeTab ${userType === "org" ? "active" : ""}`}
                  onClick={() => handleUserTypeChange("org")}
                >
                  Organisation
                </button>
              </div>
              <div className="BillingToggle">
                <button
                  type="button"
                  className={`billingTab ${billingPeriod === "monthly" ? "active" : ""
                    }`}
                  onClick={() => setBillingPeriod("monthly")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`billingTab ${billingPeriod === "annual" ? "active" : ""
                    }`}
                  onClick={() => setBillingPeriod("annual")}
                >
                  Annually
                </button>
              </div>
            </div>
            <div className={`PricingWrapper ${plans.length > 4 ? "cols-5" : ""}`}>
              {loading ? (
                <div>Loading plans...</div>
              ) : plans.length === 0 ? (
                <div>No plans available</div>
              ) : (
                <>
                  {/* Mobile Plan Titles Grid */}
                  {isMobile && (
                    <div className="mobile-plan-titles-grid">
                      {plans.map((plan, index) => (
                        <div
                          key={index}
                          className="plan-title-box"
                          onClick={() => setActivePlan(plan.name)}
                        >
                          <span className="plan-name">
                            {plan.name}
                          </span>
                          {plan.badge && (
                            <span className="plan-badge-mini">{plan.badge}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Desktop Grid / Mobile Popup */}
                  {plans.map((plan, index) => {
                    const isActive = activePlan === plan.name;

                    // On mobile, only render the active plan as a popup
                    if (isMobile && !isActive) return null;

                    return (
                      <div
                        key={index}
                        className={`PricingStandard ${isMobile ? "mobile-modal-overlay" : ""} ${isActive ? "active" : ""}`}
                      >
                        <div className={`PricingStandardCard ${isMobile ? "mobile-modal-content" : ""}`}>
                          {isMobile && (
                            <button
                              className="mobile-modal-close"
                              onClick={() => setActivePlan(null)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                          <div className="standard">
                            <h1>
                              {plan.name}
                              {plan.badge && (
                                <span className="planBadge">
                                  {plan.badge}
                                  {plan.badge === "Popular" && (
                                    <img src={proStar} alt="pro star icon" />
                                  )}
                                </span>
                              )}
                            </h1>
                            <div className="pay">
                              <strong>
                                {typeof plan.price === "number"
                                  ? formatPrice(getPlanPrice(plan.price))
                                  : plan.price}
                              </strong>
                              {typeof plan.price === "number" && (
                                <p>
                                  {billingPeriod === "monthly" ? "/ month" : "/ year"}
                                </p>
                              )}
                            </div>
                            <div className="storage">
                              <div className="storageUser">
                                <p>{userType === "org" ? "Users" : "Devices"}</p>
                                <strong>{plan.storage.users}</strong>
                              </div>
                              <div className="storageUser">
                                <p>Storage</p>
                                <strong>{plan.storage.user}</strong>
                              </div>
                              <div className="storageUser">
                                <p>Spaces</p>
                                <strong>{plan.storage.team}</strong>
                              </div>
                            </div>
                            <div className="planMeta">
                              {plan.stats?.map((stat, idx) => (
                                <div className="metaItem" key={idx}>
                                  <p>{stat.label}</p>
                                  <strong>{stat.value}</strong>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="text">
                            <p>What you will get</p>
                            <span></span>
                          </div>
                          <div className="email">
                            {plan.features.map((feature, featureIdx) => (
                              <FeatureItem
                                key={featureIdx}
                                icon={emailStar}
                                text={feature}
                              />
                            ))}
                          </div>
                          <div className="buttons">
                            {plan.freeTrialText && (
                              <Button
                                type="white"
                                title={plan.freeTrialText}
                                onClick={plan.onFreeTrialClick}
                                className="free"
                              />
                            )}
                            <Button
                              type="violet"
                              title={plan.buttonText}
                              onClick={
                                plan.isContact
                                  ? plan.onClick
                                  : () => paymentHandler(index)
                              }
                              className="get"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <Success
          title={"Account Created Successfully"}
          subTitle={
            "Your account has been successfully signed up. Enjoy exploring our services!"
          }
          buttonText={"Sign In"}
          success={true}
          buttonPlus={false}
          onClick={() => {
            navigate("/signin");
            window.location.reload();
          }}
          background={true}
        />
      )}
    </>
  );
}

export default Pricing;
